import React, { useEffect, useMemo, useState } from 'react';
import { Button, Select, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import * as api from '../api/client';
import type { FeatureRequestAnalytics, FeatureRequestItem, FeatureRequestStatus } from '../api/client';

interface Props {
  adminEmail: string;
  onOpenDetails: (id: string) => void;
  onBack?: () => void;
}

type AdminTab = 'pending' | 'active' | 'roadmap' | 'released' | 'declined' | 'analytics';

const ROADMAP_STATUSES: FeatureRequestStatus[] = ['UNDER_CONSIDERATION', 'PLANNED', 'IN_DEVELOPMENT'];
interface GovernanceKbOption {
  id: string;
  name: string;
}

const AdminFeatureGovernance: React.FC<Props> = ({ adminEmail, onOpenDetails, onBack }) => {
  const [tab, setTab] = useState<AdminTab>('pending');
  const [items, setItems] = useState<FeatureRequestItem[]>([]);
  const [analytics, setAnalytics] = useState<FeatureRequestAnalytics | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<GovernanceKbOption[]>([]);
  const [selectedKbByRequest, setSelectedKbByRequest] = useState<Record<string, string>>({});

  const load = async () => {
    try {
      const [pendingRes, allRes, analyticsRes, kbRes] = await Promise.all([
        api.getPendingFeatureRequests(adminEmail),
        api.getFeatureRequests({ sort: 'recentlyUpdated' }),
        api.getFeatureRequestAnalytics(adminEmail),
        api.listKnowledgeBases(),
      ]);
      const merged = [...(pendingRes.data.items || []), ...(allRes.data.items || [])];
      const unique = Array.from(new Map(merged.map(i => [i.id, i])).values());
      setItems(unique);
      setAnalytics(analyticsRes.data);
      const kbOptions = (kbRes.data?.knowledge_bases || [])
        .filter((kb: any) => (kb?.status || '').toUpperCase() === 'ACTIVE')
        .map((kb: any) => ({ id: String(kb.id), name: String(kb.name || kb.id) }));
      setKnowledgeBases(kbOptions);
      setSelectedKbByRequest(prev => {
        const next = { ...prev };
        unique.forEach((item) => {
          if (!next[item.id]) {
            next[item.id] = item.assignedKnowledgeBaseId || '';
          }
        });
        return next;
      });
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load governance data.');
    }
  };

  useEffect(() => { if (adminEmail) void load(); }, [adminEmail]);

  const filtered = useMemo(() => {
    if (tab === 'pending') return items.filter(i => ['PENDING_REVIEW', 'NEEDS_CLARIFICATION'].includes(i.status));
    if (tab === 'active') return items.filter(i => i.status === 'APPROVED_FOR_VOTING');
    if (tab === 'roadmap') return items.filter(i => ROADMAP_STATUSES.includes(i.status));
    if (tab === 'released') return items.filter(i => i.status === 'RELEASED');
    if (tab === 'declined') return items.filter(i => ['DECLINED', 'MERGED', 'ARCHIVED'].includes(i.status));
    return [];
  }, [tab, items]);

  const approve = async (id: string) => { await api.approveFeatureRequest(id, adminEmail); await load(); };
  const reject = async (id: string) => {
    const reason = window.prompt('Rejection reason (required):', '');
    if (!reason?.trim()) return;
    await api.rejectFeatureRequest(id, { rejectionReason: reason.trim() }, adminEmail);
    await load();
  };
  const clarify = async (id: string) => {
    const note = window.prompt('Clarification note (required):', '');
    if (!note?.trim()) return;
    await api.requestFeatureClarification(id, { note: note.trim() }, adminEmail);
    await load();
  };
  const setStatus = async (id: string, status: FeatureRequestStatus) => {
    await api.updateFeatureRequestStatus(id, { status }, adminEmail);
    await load();
  };
  const assignKb = async (id: string) => {
    const kbId = (selectedKbByRequest[id] || '').trim();
    if (!kbId) {
      message.warning('Select a knowledge base first.');
      return;
    }
    await api.assignFeatureRequestKnowledgeBase(id, { knowledgeBaseId: kbId }, adminEmail);
    message.success('Knowledge base assigned.');
    await load();
  };
  const generateCopilotTask = async (id: string) => {
    await api.generateFeatureRequestCopilotTask(id, adminEmail);
    message.success('Copilot prompt task started.');
    await load();
  };
  const merge = async (id: string) => {
    const target = window.prompt('Target feature request id:', '');
    if (!target?.trim()) return;
    await api.mergeFeatureRequest(id, { targetFeatureRequestId: target.trim(), transferComments: true }, adminEmail);
    await load();
  };

  return (
    <div className="praxys-view-panel">
      <div className="praxys-view-header">
        {onBack && (
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 12 }}>
            Back to Admin
          </Button>
        )}
        <h2 className="praxys-view-title">Feature Governance</h2>
      </div>

      <div className="praxys-fr-admin-tabs">
        {[
          ['pending', 'Pending Review'],
          ['active', 'Active Voting'],
          ['roadmap', 'Roadmap'],
          ['released', 'Released'],
          ['declined', 'Declined / Merged'],
          ['analytics', 'Analytics'],
        ].map(([key, label]) => (
          <button key={key} className={`praxys-nav-row-btn${tab === key ? ' active' : ''}`} onClick={() => setTab(key as AdminTab)}>{label}</button>
        ))}
      </div>

      {tab === 'analytics' ? (
        <div className="praxys-fr-analytics-grid">
          <div className="praxys-admin-metric-card"><div className="praxys-admin-metric-value">{analytics?.total_pending ?? 0}</div><div className="praxys-admin-metric-label">Pending Review</div></div>
          <div className="praxys-admin-metric-card"><div className="praxys-admin-metric-value">{analytics?.total_approved ?? 0}</div><div className="praxys-admin-metric-label">Approved</div></div>
          <div className="praxys-admin-metric-card"><div className="praxys-admin-metric-value">{analytics?.trending_requests?.length ?? 0}</div><div className="praxys-admin-metric-label">Trending</div></div>
          <div className="praxys-admin-metric-card"><div className="praxys-admin-metric-value">{analytics?.average_approval_time_hours ?? 0}h</div><div className="praxys-admin-metric-label">Avg Approval Time</div></div>
          <div className="praxys-fr-analytics-list">
            <h4>Top Categories</h4>
            {(analytics?.top_categories || []).map(c => <div key={c.category}>{c.category}: {c.count}</div>)}
            <h4 style={{ marginTop: 12 }}>Most Voted</h4>
            {(analytics?.most_voted_requests || []).map(f => <div key={f.id}>{f.title} ({f.vote_count})</div>)}
          </div>
        </div>
      ) : (
        <div className="praxys-fr-table">
          <div className="praxys-fr-table-head">
            <span>Title</span><span>Created by</span><span>Category</span><span>Impact</span><span>Created</span><span>KB</span><span>Actions</span>
          </div>
          {filtered.map(i => (
            <div key={i.id} className="praxys-fr-table-row">
              <span>{i.title}</span>
              <span>{i.createdByName || i.createdByUserId}</span>
              <span>{i.category}</span>
              <span>{i.impactLevel}</span>
              <span>{new Date(i.createdOn).toLocaleDateString()}</span>
              <span>{i.assignedKnowledgeBaseName || '—'}</span>
              <span className="praxys-fr-inline-actions">
                <button onClick={() => onOpenDetails(i.id)}>View</button>
                {tab === 'pending' && <button onClick={() => void approve(i.id)}>Approve</button>}
                {tab === 'pending' && <button onClick={() => void reject(i.id)}>Reject</button>}
                {tab === 'pending' && <button onClick={() => void clarify(i.id)}>Needs Clarification</button>}
                {tab === 'pending' && <button onClick={() => void merge(i.id)}>Merge</button>}
                {tab !== 'pending' && (
                  <>
                    <Select
                      className="praxys-fr-actions-select"
                      placeholder="Assign KB"
                      value={selectedKbByRequest[i.id] || undefined}
                      onChange={(value) => setSelectedKbByRequest(prev => ({ ...prev, [i.id]: value as string }))}
                      options={knowledgeBases.map(kb => ({ value: kb.id, label: kb.name }))}
                      allowClear
                    />
                    <button onClick={() => void assignKb(i.id)}>Assign KB</button>
                  </>
                )}
                {tab !== 'pending' && tab !== 'released' && (
                  <Select
                    className="praxys-fr-actions-select"
                    value={i.status}
                    onChange={value => void setStatus(i.id, value as FeatureRequestStatus)}
                    options={[
                      { value: 'UNDER_CONSIDERATION', label: 'Under Consideration' },
                      { value: 'PLANNED', label: 'Planned' },
                      { value: 'IN_DEVELOPMENT', label: 'In Development' },
                      { value: 'RELEASED', label: 'Released' },
                      { value: 'ARCHIVED', label: 'Archived' },
                    ]}
                  />
                )}
                {i.status === 'PLANNED' && (
                  <button onClick={() => void generateCopilotTask(i.id)}>Generate Copilot Prompt</button>
                )}
              </span>
            </div>
          ))}
          {filtered.length === 0 && <div className="praxys-empty">No items in this tab.</div>}
        </div>
      )}
    </div>
  );
};

export default AdminFeatureGovernance;
